import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface KYTAnalysisData {
  address: string;
  blockchain: string;
  addressType: string;
  riskScore: number;
  riskLevel: string;
  riskFactors: Record<string, any>;
  isSanctioned: boolean;
  isExchange: boolean;
  exchangeName?: string;
  totalReceived: number;
  totalSent: number;
  netBalance: number;
  transactionCount: number;
  firstSeen: string;
  lastUpdated: string;
  transactions: any[];
  analysisTimestamp: string;
}

interface TraceData {
  rootAddress: string;
  traceSteps: any[];
  totalAmount: number;
  riskScore: number;
  summary: {
    totalNodes: number;
    inConnections: number;
    outConnections: number;
    totalConnections: number;
  };
}

export class PDFExportService {
  private doc: jsPDF;
  private currentY: number = 20;
  private pageHeight: number = 280; // A4 page height in mm
  private margin: number = 20;

  constructor() {
    this.doc = new jsPDF('p', 'mm', 'a4');
  }

  async generateKYTReport(
    analysisData: KYTAnalysisData,
    traceData: TraceData,
    spiderChartElement: HTMLElement | null,
    flowChartElement: HTMLElement | null
  ): Promise<void> {
    try {
      // Add header
      this.addHeader();
      
      // Add executive summary
      this.addExecutiveSummary(analysisData);
      
      // Add address analysis
      this.addAddressAnalysis(analysisData);
      
      // Add risk assessment
      this.addRiskAssessment(analysisData);
      
      // Add transaction summary
      this.addTransactionSummary(analysisData);
      
      // Add charts - capture the currently visible chart
      let chartsAdded = 0;
      
      if (spiderChartElement) {
        await this.addChart(spiderChartElement, 'Transaction Flow Network (Spider Diagram)', traceData);
        chartsAdded++;
      }
      
      if (flowChartElement) {
        await this.addChart(flowChartElement, 'Transaction Flow Network (Flow Diagram)', traceData);
        chartsAdded++;
      }
      
      // Add note about which chart is included
      if (chartsAdded > 0) {
        this.addSectionHeader('CHART INFORMATION');
        this.doc.setFontSize(9);
        this.doc.setFont('helvetica', 'normal');
        if (spiderChartElement && flowChartElement) {
          this.doc.text('This report includes both Spider Network and Flow Diagram visualizations.', this.margin, this.currentY);
        } else if (spiderChartElement) {
          this.doc.text('This report includes the Spider Network diagram. Switch to Flow Diagram tab to include that visualization.', this.margin, this.currentY);
        } else if (flowChartElement) {
          this.doc.text('This report includes the Flow Diagram. Switch to Spider Network tab to include that visualization.', this.margin, this.currentY);
        }
        this.currentY += 10;
      }
      
      // Add comprehensive text-based analysis for missing charts or as supplement
      if (chartsAdded === 0) {
        this.addSectionHeader('TRANSACTION FLOW ANALYSIS');
        this.addComprehensiveTextAnalysis(traceData);
      } else if (chartsAdded === 1) {
        // Add text analysis for the missing chart
        this.addSectionHeader('ADDITIONAL FLOW ANALYSIS');
        this.addComprehensiveTextAnalysis(traceData);
      } else {
        console.log(`Successfully added ${chartsAdded} chart(s) to PDF report`);
      }
      
      // Add detailed transaction history
      this.addTransactionHistory(analysisData);
      
      // Add compliance recommendations
      this.addComplianceRecommendations(analysisData);
      
      // Add footer
      this.addFooter();
      
      // Save the PDF with unique filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0] + '_' + 
                       new Date().toISOString().replace(/[:.]/g, '-').split('T')[1].substring(0, 8);
      const fileName = `KYT_Report_${analysisData.address.substring(0, 8)}_${timestamp}.pdf`;
      this.doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw new Error('Failed to generate PDF report');
    }
  }

  private addHeader(): void {
    // Company logo and title
    this.doc.setFontSize(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('KYT INVESTIGATION REPORT', this.margin, this.currentY);
    
    this.currentY += 10;
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Know Your Transaction Analysis', this.margin, this.currentY);
    
    this.currentY += 15;
    
    // Report metadata
    this.doc.setFontSize(10);
    this.doc.text(`Report Generated: ${new Date().toLocaleString()}`, this.margin, this.currentY);
    this.doc.text(`Report ID: KYT-${Date.now()}`, this.margin + 100, this.currentY);
    
    this.currentY += 20;
    
    // Add a line separator
    this.doc.setDrawColor(0, 0, 0);
    this.doc.line(this.margin, this.currentY, 190, this.currentY);
    this.currentY += 10;
  }

  private addExecutiveSummary(analysisData: KYTAnalysisData): void {
    this.addSectionHeader('EXECUTIVE SUMMARY');
    
    const summary = `This report presents a comprehensive Know Your Transaction (KYT) analysis for the cryptocurrency address ${analysisData.address} on the ${analysisData.blockchain} blockchain. The analysis reveals a ${analysisData.riskLevel.toLowerCase()} risk profile with a risk score of ${analysisData.riskScore}%.`;
    
    this.addWrappedText(summary);
    this.currentY += 10;
    
    // Key findings box
    this.doc.setFillColor(240, 240, 240);
    this.doc.rect(this.margin, this.currentY, 150, 25, 'F');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('KEY FINDINGS:', this.margin + 5, this.currentY + 7);
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`• Risk Level: ${analysisData.riskLevel}`, this.margin + 5, this.currentY + 12);
    this.doc.text(`• Total Transactions: ${analysisData.transactionCount}`, this.margin + 5, this.currentY + 17);
    this.doc.text(`• Net Balance: $${analysisData.netBalance.toFixed(2)}`, this.margin + 5, this.currentY + 22);
    
    this.currentY += 35;
  }

  private addAddressAnalysis(analysisData: KYTAnalysisData): void {
    this.addSectionHeader('ADDRESS ANALYSIS');
    
    const addressInfo = [
      ['Address', analysisData.address],
      ['Blockchain', analysisData.blockchain],
      ['Address Type', analysisData.addressType],
      ['First Seen', new Date(analysisData.firstSeen).toLocaleDateString()],
      ['Last Updated', new Date(analysisData.lastUpdated).toLocaleDateString()],
      ['Sanctioned', analysisData.isSanctioned ? 'YES' : 'NO'],
      ['Exchange', analysisData.isExchange ? 'YES' : 'NO']
    ];
    
    this.addTable(addressInfo);
    this.currentY += 10;
  }

  private addRiskAssessment(analysisData: KYTAnalysisData): void {
    this.addSectionHeader('RISK ASSESSMENT');
    
    // Risk score with color coding
    const riskColor = this.getRiskColor(analysisData.riskLevel);
    this.doc.setFillColor(riskColor.r, riskColor.g, riskColor.b);
    this.doc.rect(this.margin, this.currentY, 30, 15, 'F');
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(`${analysisData.riskScore}%`, this.margin + 15, this.currentY + 10);
    
    this.doc.setTextColor(0, 0, 0);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`Risk Level: ${analysisData.riskLevel}`, this.margin + 40, this.currentY + 10);
    
    this.currentY += 25;
    
    // Risk assessment summary
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Risk Assessment Summary:', this.margin, this.currentY);
    this.currentY += 5;
    
    const riskSummary = this.getRiskSummary(analysisData);
    this.doc.setFont('helvetica', 'normal');
    this.addWrappedText(riskSummary);
    this.currentY += 10;
    
    // Risk factors
    if (analysisData.riskFactors && Object.keys(analysisData.riskFactors).length > 0) {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Detailed Risk Factors:', this.margin, this.currentY);
      this.currentY += 5;
      
      Object.entries(analysisData.riskFactors).forEach(([key, value]) => {
        this.doc.setFont('helvetica', 'normal');
        this.doc.text(`• ${key}: ${value}`, this.margin + 5, this.currentY);
        this.currentY += 5;
      });
    }
    
    this.currentY += 10;
  }

  private addTransactionSummary(analysisData: KYTAnalysisData): void {
    this.addSectionHeader('TRANSACTION SUMMARY');
    
    const transactionInfo = [
      ['Total Received', `$${analysisData.totalReceived.toFixed(2)}`],
      ['Total Sent', `$${analysisData.totalSent.toFixed(2)}`],
      ['Net Balance', `$${analysisData.netBalance.toFixed(2)}`],
      ['Transaction Count', analysisData.transactionCount.toString()]
    ];
    
    this.addTable(transactionInfo);
    this.currentY += 10;
  }

  private async addChart(chartElement: HTMLElement, title: string, traceData?: TraceData): Promise<void> {
    if (this.currentY > this.pageHeight - 100) {
      this.doc.addPage();
      this.currentY = 20;
    }
    
    this.addSectionHeader(title);
    
    try {
      // Wait a bit for the chart to render
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log(`=== CHART CAPTURE DEBUG START ===`);
      console.log(`Capturing chart: ${title}`);
      console.log(`Element dimensions: ${chartElement.offsetWidth}x${chartElement.offsetHeight}`);
      console.log(`Element display: ${window.getComputedStyle(chartElement).display}`);
      console.log(`Element visibility: ${window.getComputedStyle(chartElement).visibility}`);
      console.log(`Element position: ${chartElement.offsetLeft}, ${chartElement.offsetTop}`);
      
      // Ensure the element is visible and has content
      if (chartElement.offsetWidth === 0 || chartElement.offsetHeight === 0) {
        throw new Error('Chart element has no visible dimensions');
      }
      
      // Try to find the SVG element within the chart container
      const svgElement = chartElement.querySelector('svg');
      if (!svgElement) {
        console.log(`❌ No SVG element found in chart container`);
        console.log(`Available child elements:`, Array.from(chartElement.children).map(el => el.tagName));
        throw new Error('No SVG element found in chart container');
      }
      
      console.log(`✅ SVG element found:`, svgElement);
      
      // Get SVG dimensions using getBoundingClientRect
      const svgRect = svgElement.getBoundingClientRect();
      console.log(`SVG dimensions: ${svgRect.width}x${svgRect.height}`);
      console.log(`SVG position: ${svgRect.left}, ${svgRect.top}`);
      console.log(`SVG viewBox: ${svgElement.getAttribute('viewBox')}`);
      console.log(`SVG width/height attributes: ${svgElement.getAttribute('width')} x ${svgElement.getAttribute('height')}`);
      
      // Check if SVG has content
      const svgChildren = svgElement.children;
      console.log(`SVG children count: ${svgChildren.length}`);
      if (svgChildren.length > 0) {
        console.log(`SVG children:`, Array.from(svgChildren).map(el => el.tagName));
      }
      
      // Capture the SVG element directly (cast through unknown to avoid type issues)
      console.log(`Starting html2canvas capture...`);
      const canvas = await html2canvas(svgElement as unknown as HTMLElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: true,
        width: svgRect.width,
        height: svgRect.height,
        scrollX: 0,
        scrollY: 0
      });
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas has zero dimensions');
      }
      
      console.log(`✅ Canvas created: ${canvas.width}x${canvas.height}`);
      
      const imgData = canvas.toDataURL('image/png', 0.95);
      const imgWidth = 150;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add the image to PDF
      this.doc.addImage(imgData, 'PNG', this.margin, this.currentY, imgWidth, imgHeight);
      this.currentY += imgHeight + 10;
      
      console.log(`✅ Chart captured successfully: ${title}`);
      console.log(`=== CHART CAPTURE DEBUG END ===`);
      
    } catch (error) {
      console.error('❌ Error capturing chart:', error);
      console.log(`=== CHART CAPTURE DEBUG END (ERROR) ===`);
      
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(`Chart could not be captured: ${title}`, this.margin, this.currentY);
      this.doc.text(`Error: ${error instanceof Error ? error.message : String(error)}`, this.margin, this.currentY + 5);
      
      // Add a text-based representation of the chart data
      this.addTextBasedChartRepresentation(title, traceData);
      this.currentY += 20;
    }
  }

  private addComprehensiveTextAnalysis(traceData: TraceData): void {
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    // Network Overview
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Network Overview:', this.margin, this.currentY);
    this.currentY += 5;
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`• Root Address: ${traceData.rootAddress}`, this.margin + 5, this.currentY);
    this.currentY += 4;
    this.doc.text(`• Total Steps Analyzed: ${traceData.traceSteps?.length || 0}`, this.margin + 5, this.currentY);
    this.currentY += 4;
    this.doc.text(`• Total Amount: $${traceData.totalAmount.toFixed(2)}`, this.margin + 5, this.currentY);
    this.currentY += 4;
    this.doc.text(`• Network Risk Score: ${traceData.riskScore}%`, this.margin + 5, this.currentY);
    this.currentY += 8;
    
    // Connection Statistics
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Connection Statistics:', this.margin, this.currentY);
    this.currentY += 5;
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`• Total Nodes: ${traceData.summary.totalNodes}`, this.margin + 5, this.currentY);
    this.currentY += 4;
    this.doc.text(`• Total Connections: ${traceData.summary.totalConnections}`, this.margin + 5, this.currentY);
    this.currentY += 4;
    this.doc.text(`• Incoming Connections: ${traceData.summary.inConnections}`, this.margin + 5, this.currentY);
    this.currentY += 4;
    this.doc.text(`• Outgoing Connections: ${traceData.summary.outConnections}`, this.margin + 5, this.currentY);
    this.currentY += 8;
    
    // Transaction Flow Summary
    if (traceData.traceSteps && traceData.traceSteps.length > 0) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Transaction Flow Summary:', this.margin, this.currentY);
      this.currentY += 5;
      
      this.doc.setFont('helvetica', 'normal');
      
      // Group by step
      const stepsByLevel = traceData.traceSteps.reduce((acc, step) => {
        const level = step.step || 1;
        if (!acc[level]) acc[level] = [];
        acc[level].push(step);
        return acc;
      }, {} as Record<number, any[]>);
      
      Object.entries(stepsByLevel).forEach(([level, steps]) => {
        const stepsArray = steps as any[];
        this.doc.text(`Level ${level}: ${stepsArray.length} transactions`, this.margin + 5, this.currentY);
        this.currentY += 4;
        
        // Show first few addresses in each level
        stepsArray.slice(0, 3).forEach((step: any, index: number) => {
          const address = step.fromAddress || step.toAddress || step.address;
          if (address) {
            this.doc.text(`  ${index + 1}. ${address.substring(0, 12)}...`, this.margin + 10, this.currentY);
            this.currentY += 3;
          }
        });
        
        if (stepsArray.length > 3) {
          this.doc.text(`  ... and ${stepsArray.length - 3} more`, this.margin + 10, this.currentY);
          this.currentY += 3;
        }
        this.currentY += 2;
      });
    }
    
    this.currentY += 10;
  }

  private addTextBasedChartRepresentation(title: string, traceData?: TraceData): void {
    this.currentY += 10;
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Chart Data Summary:', this.margin, this.currentY);
    this.currentY += 5;
    
    this.doc.setFont('helvetica', 'normal');
    if (title.includes('Spider')) {
      this.doc.text('• Network visualization showing all connected addresses', this.margin + 5, this.currentY);
      this.currentY += 4;
      this.doc.text('• Nodes represent addresses with risk level color coding', this.margin + 5, this.currentY);
      this.currentY += 4;
      this.doc.text('• Lines show transaction relationships and amounts', this.margin + 5, this.currentY);
      
      if (traceData) {
        this.currentY += 8;
        this.doc.setFont('helvetica', 'bold');
        this.doc.text('Network Statistics:', this.margin + 5, this.currentY);
        this.currentY += 4;
        this.doc.setFont('helvetica', 'normal');
        this.doc.text(`• Total Nodes: ${traceData.summary.totalNodes}`, this.margin + 10, this.currentY);
        this.currentY += 4;
        this.doc.text(`• Total Connections: ${traceData.summary.totalConnections}`, this.margin + 10, this.currentY);
        this.currentY += 4;
        this.doc.text(`• Incoming Connections: ${traceData.summary.inConnections}`, this.margin + 10, this.currentY);
        this.currentY += 4;
        this.doc.text(`• Outgoing Connections: ${traceData.summary.outConnections}`, this.margin + 10, this.currentY);
      }
    } else if (title.includes('Flow')) {
      this.doc.text('• Sequential flow diagram showing transaction levels', this.margin + 5, this.currentY);
      this.currentY += 4;
      this.doc.text('• Each level represents transaction distance from root', this.margin + 5, this.currentY);
      this.currentY += 4;
      this.doc.text('• Shows money flow patterns and transaction chains', this.margin + 5, this.currentY);
      
      if (traceData && traceData.traceSteps) {
        this.currentY += 8;
        this.doc.setFont('helvetica', 'bold');
        this.doc.text('Flow Statistics:', this.margin + 5, this.currentY);
        this.currentY += 4;
        this.doc.setFont('helvetica', 'normal');
        this.doc.text(`• Total Steps: ${traceData.traceSteps.length}`, this.margin + 10, this.currentY);
        this.currentY += 4;
        this.doc.text(`• Total Amount: $${traceData.totalAmount.toFixed(2)}`, this.margin + 10, this.currentY);
        this.currentY += 4;
        this.doc.text(`• Risk Score: ${traceData.riskScore}%`, this.margin + 10, this.currentY);
      }
    }
    this.currentY += 5;
  }

  private addTransactionHistory(analysisData: KYTAnalysisData): void {
    if (!analysisData.transactions || analysisData.transactions.length === 0) {
      return;
    }
    
    this.addSectionHeader('RECENT TRANSACTION HISTORY');
    
    // Show first 10 transactions
    const recentTransactions = analysisData.transactions.slice(0, 10);
    
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'bold');
    
    // Table header
    this.doc.text('Hash', this.margin, this.currentY);
    this.doc.text('From', this.margin + 30, this.currentY);
    this.doc.text('To', this.margin + 80, this.currentY);
    this.doc.text('Amount', this.margin + 130, this.currentY);
    this.doc.text('Date', this.margin + 160, this.currentY);
    
    this.currentY += 5;
    
    // Table rows
    this.doc.setFont('helvetica', 'normal');
    recentTransactions.forEach((tx, index) => {
      if (this.currentY > this.pageHeight - 20) {
        this.doc.addPage();
        this.currentY = 20;
      }
      
      this.doc.text(tx.transactionHash.substring(0, 8) + '...', this.margin, this.currentY);
      this.doc.text(tx.fromAddress ? tx.fromAddress.substring(0, 8) + '...' : 'N/A', this.margin + 30, this.currentY);
      this.doc.text(tx.toAddress ? tx.toAddress.substring(0, 8) + '...' : 'N/A', this.margin + 80, this.currentY);
      this.doc.text(`$${tx.amount.toFixed(2)}`, this.margin + 130, this.currentY);
      this.doc.text(new Date(tx.timestamp).toLocaleDateString(), this.margin + 160, this.currentY);
      
      this.currentY += 5;
    });
    
    this.currentY += 10;
  }

  private addComplianceRecommendations(analysisData: KYTAnalysisData): void {
    this.addSectionHeader('COMPLIANCE RECOMMENDATIONS');
    
    const recommendations = this.getComplianceRecommendations(analysisData);
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    recommendations.forEach((rec, index) => {
      this.doc.text(`${index + 1}. ${rec}`, this.margin, this.currentY);
      this.currentY += 5;
    });
    
    this.currentY += 10;
  }

  private addFooter(): void {
    const pageCount = this.doc.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      
      // Footer line
      this.doc.setDrawColor(0, 0, 0);
      this.doc.line(this.margin, 280, 190, 280);
      
      // Footer text
      this.doc.setFontSize(8);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text('Confidential KYT Investigation Report', this.margin, 285);
      this.doc.text(`Page ${i} of ${pageCount}`, 150, 285);
      this.doc.text('Generated by Banking App KYT System', this.margin, 290);
    }
  }

  private addSectionHeader(title: string): void {
    if (this.currentY > this.pageHeight - 30) {
      this.doc.addPage();
      this.currentY = 20;
    }
    
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin, this.currentY);
    this.currentY += 8;
    
    // Underline
    this.doc.setDrawColor(0, 0, 0);
    this.doc.line(this.margin, this.currentY, this.margin + 50, this.currentY);
    this.currentY += 10;
  }

  private addWrappedText(text: string): void {
    const maxWidth = 150;
    const lines = this.doc.splitTextToSize(text, maxWidth);
    
    lines.forEach((line: string) => {
      if (this.currentY > this.pageHeight - 20) {
        this.doc.addPage();
        this.currentY = 20;
      }
      
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(line, this.margin, this.currentY);
      this.currentY += 5;
    });
  }

  private addTable(data: string[][]): void {
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    
    data.forEach(([key, value]) => {
      if (this.currentY > this.pageHeight - 20) {
        this.doc.addPage();
        this.currentY = 20;
      }
      
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(key + ':', this.margin, this.currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(value, this.margin + 60, this.currentY);
      this.currentY += 5;
    });
  }

  private getRiskColor(riskLevel: string): { r: number; g: number; b: number } {
    switch (riskLevel.toUpperCase()) {
      case 'LOW': return { r: 76, g: 175, b: 80 };
      case 'MEDIUM': return { r: 255, g: 152, b: 0 };
      case 'HIGH': return { r: 244, g: 67, b: 54 };
      case 'CRITICAL': return { r: 156, g: 39, b: 176 };
      default: return { r: 158, g: 158, b: 158 };
    }
  }

  private getRiskSummary(analysisData: KYTAnalysisData): string {
    const riskLevel = analysisData.riskLevel.toUpperCase();
    const riskScore = analysisData.riskScore;
    
    let summary = `The address has been assessed with a ${riskLevel} risk level (${riskScore}% risk score). `;
    
    if (riskLevel === 'LOW') {
      summary += 'This address presents minimal risk factors and appears to be associated with normal cryptocurrency activity. ';
    } else if (riskLevel === 'MEDIUM') {
      summary += 'This address shows some risk indicators that warrant monitoring but does not present immediate concerns. ';
    } else if (riskLevel === 'HIGH') {
      summary += 'This address exhibits significant risk factors that require enhanced monitoring and due diligence. ';
    } else if (riskLevel === 'CRITICAL') {
      summary += 'This address presents critical risk factors that require immediate attention and enhanced verification procedures. ';
    }
    
    if (analysisData.isSanctioned) {
      summary += 'CRITICAL: This address is on a sanctions list and should be blocked immediately. ';
    }
    
    if (analysisData.isExchange) {
      summary += 'This address is associated with a cryptocurrency exchange. ';
    }
    
    if (analysisData.transactionCount > 1000) {
      summary += 'High transaction volume detected, indicating active usage. ';
    }
    
    if (Math.abs(analysisData.netBalance) > 100000) {
      summary += 'Large balance detected, requiring enhanced monitoring. ';
    }
    
    return summary;
  }

  private getComplianceRecommendations(analysisData: KYTAnalysisData): string[] {
    const recommendations: string[] = [];
    
    if (analysisData.isSanctioned) {
      recommendations.push('IMMEDIATE ACTION REQUIRED: Address is on sanctions list - block all transactions');
    }
    
    if (analysisData.riskLevel === 'CRITICAL') {
      recommendations.push('Enhanced due diligence required for all transactions involving this address');
    }
    
    if (analysisData.riskLevel === 'HIGH') {
      recommendations.push('Monitor all transactions and consider enhanced verification procedures');
    }
    
    if (analysisData.isExchange) {
      recommendations.push('Verify exchange compliance and regulatory status');
    }
    
    if (analysisData.transactionCount > 1000) {
      recommendations.push('High transaction volume detected - monitor for unusual patterns');
    }
    
    if (analysisData.netBalance > 100000) {
      recommendations.push('Large balance detected - consider enhanced monitoring');
    }
    
    recommendations.push('Regular risk assessment updates recommended');
    recommendations.push('Maintain transaction records for compliance purposes');
    
    return recommendations;
  }
}

export const pdfExportService = new PDFExportService();
